import { useEffect, useId, useRef, useState } from 'react';

import { initials, PersonAvatar } from 'components/person-avatar';
import { useAuth } from 'context/AuthContext';
import { TaskItemProps, useTaskItemEcoa } from 'components/task-item-ecoa/useTaskItemEcoa';
import { HUMANOGRAMA, PilulaDaEtapa } from 'pages/RealizacaoDestaque/components/AgendaDaRealizacao';
import { rostoDoAutor } from 'pages/RealizacaoDestaque/identidade-do-participante';
import { sairDaRealizacao } from 'services/participacao.service';
import { FOCO_VISIVEL } from 'utils/foco';

import { eixoDaAcao, FaseEcoa } from '../acoes-do-semestre';
import { etapasDaAcaoEcoa } from '../andamento-da-acao';
import { FALHA_NA_ACAO, ModaisDaAcaoEcoa } from './comum-dos-cartoes';
import DetalhesDaAcaoEcoa from './DetalhesDaAcaoEcoa';
import ExclusaoDoIntegrante from './ExclusaoDoIntegrante';

export const AVISO_DE_SAIDA = 'Você saiu desta Ação ECOA.';

const BOTAO_DO_PAINEL = `rounded-jj-md border-0 px-4 py-[9px] text-[10.5px] font-bold uppercase tracking-[0.04em] aria-disabled:opacity-50 ${FOCO_VISIVEL}`;

export interface CartaoDeParticipacaoEcoaProps {
  data: any;
  /** Fase mensal em que a ação foi cadastrada. */
  fase: FaseEcoa | undefined;
  rotuloDaFase: string;
  playerInfo: any;
  /** Saída confirmada (`status === 'OK'`): a página anuncia o aviso e relê o ciclo (ECN-15, ECN-63). */
  onConcluida: (aviso: string) => void;
  /** Depois de uma ação dos modais do "Ver mais": a página relê o ciclo. */
  onRecarregar: () => void;
}

/**
 * Cartão de uma Ação ECOA em que o integrante foi incluído (ECN-13 a ECN-16): "Sair da ação" aparece na condição
 * `canExcludeParticipation` do `TaskItemEcoa` para a linha do próprio usuário e grava como o modal de hoje.
 * Não há "Reduzir percentual": a ECOA não tem percentual de atuação.
 */
export default function CartaoDeParticipacaoEcoa({ data, fase, rotuloDaFase, playerInfo, onConcluida, onRecarregar }: CartaoDeParticipacaoEcoaProps) {
  const modelo = useTaskItemEcoa({ data, cycle: fase as TaskItemProps['cycle'], playerInfo, onReload: onRecarregar, ecoa: true });
  // Mesma chave que o `canExcludeParticipation` compara: a do usuário logado.
  const chave: string | undefined = useAuth().user?._id;
  // O `TaskItemEcoa` só oferece a saída na linha do usuário entre os integrantes (`target`).
  const eu = Array.isArray(data.target) ? data.target.find((pessoa: any) => pessoa._id === chave) : undefined;
  const podeSair = !!eu && modelo.canExcludeParticipation(eu);
  const statusAtual = etapasDaAcaoEcoa(modelo).find(etapa => etapa.atual)?.status;
  const responsavel = data.original_manager_detail?.nome_guerra ? modelo.capitalizeWords(data.original_manager_detail.nome_guerra) : undefined;

  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [falhou, setFalhou] = useState(false);
  const tituloId = useId();
  const detalhesId = useId();
  const textoDaConfirmacao = useRef<HTMLParagraphElement>(null);
  const botaoSair = useRef<HTMLButtonElement>(null);
  const estavaConfirmando = useRef(false);

  // O botão que abre a confirmação sai da tela: o foco vai ao texto dela e, ao voltar, a quem a abriu (ECN-65).
  useEffect(() => {
    if (confirmando) textoDaConfirmacao.current?.focus();
    else if (estavaConfirmando.current) botaoSair.current?.focus();
    estavaConfirmando.current = confirmando;
  }, [confirmando]);

  function abrir() {
    setFalhou(false);
    setConfirmando(true);
  }

  // Voltar durante a gravação esconderia o desfecho dela.
  function voltar() {
    if (!enviando) setConfirmando(false);
  }

  async function sair() {
    if (enviando) return;
    setEnviando(true);
    setFalhou(false);
    const saiu = await sairDaRealizacao(data._id, chave as string, modelo.ecoa);
    setEnviando(false);

    if (saiu) {
      // A página relê o ciclo e anuncia o aviso; o cartão sai da lista.
      estavaConfirmando.current = false;
      setConfirmando(false);
      onConcluida(AVISO_DE_SAIDA);
    } else {
      setFalhou(true);
    }
  }

  return (
    // SPEC_DEVIATION: fundo `brand-white` (o cartão vinha sem fundo, sobre o `surface-app` da página). Reason: ECN-61, o
    // `content-soft` sobre `surface-app` fica em 4,43:1 e o `brand-blue` em 4,35:1 (src/test-utils/contraste.ts).
    <article
      aria-labelledby={tituloId}
      className={`flex flex-col gap-3 rounded-jj-lg border bg-brand-white px-[18px] py-4 shadow-[0_1px_3px_rgba(17,24,39,.06)] ${
        confirmando ? 'border-brand-blue' : 'border-surface-300'
      }`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <PersonAvatar
          {...rostoDoAutor(data)}
          initials={initials(data.owner_name_guerra || data.owner_name || data.title || '')}
          size={40}
          initialsClassName="bg-brand-green font-display text-xs font-bold text-brand-navy"
        />
        <div className="min-w-0 flex-1">
          <h3 id={tituloId} className="m-0 font-display text-[14.5px] font-bold leading-[1.3] text-brand-blue-dark">
            {data.title}
          </h3>
          <p className="m-0 mt-1 text-[11.5px] text-content-soft">
            Cadastrada por{' '}
            <a
              href={`${HUMANOGRAMA}${data.owner}`}
              target="_blank"
              rel="noreferrer"
              className={`font-bold text-link hover:text-link-hover ${FOCO_VISIVEL}`}
            >
              {`${data.owner_name_guerra ?? ''} (${data.owner})`}
            </a>
            {` · ${eixoDaAcao(data)}`}
          </p>
          <p className="m-0 mt-0.5 text-[11px] text-content-soft">{rotuloDaFase}</p>
        </div>
        <PilulaDaEtapa status={statusAtual} />
        <dl className="m-0 text-right">
          <dt className="text-[10px] text-content-soft">Pontos</dt>
          <dd className="m-0 font-display text-base font-bold text-brand-blue">{modelo.points}</dd>
        </dl>
      </div>

      {podeSair && !confirmando && (
        <div className="flex border-t border-surface-100 pt-3">
          {/* SPEC_DEVIATION: `text-urgent` no lugar do `brand-pink` do design (2,9:1 no branco). */}
          <button
            ref={botaoSair}
            type="button"
            onClick={abrir}
            className={`ml-auto cursor-pointer border-0 bg-transparent p-0 text-[11px] font-bold uppercase tracking-[0.04em] text-urgent hover:underline ${FOCO_VISIVEL}`}
          >
            Sair da ação
          </button>
        </div>
      )}

      {confirmando && (
        <div className="flex flex-wrap items-center gap-3.5 rounded-jj-md bg-brand-pink-light px-4 py-3.5">
          <p ref={textoDaConfirmacao} tabIndex={-1} className="m-0 flex-[1_1_260px] text-[11.5px] leading-[1.55] text-brand-navy outline-none">
            <b>Tem certeza?</b> Ao sair, você deixa de receber os pontos desta ação. Só quem cadastrou pode incluir você de novo.
          </p>
          <div className="flex gap-2.5">
            <button type="button" aria-disabled={enviando || undefined} onClick={voltar} className={`${BOTAO_DO_PAINEL} bg-brand-white text-brand-blue-dark`}>
              Voltar
            </button>
            {/* SPEC_DEVIATION: fundo `urgent` no lugar do `brand-pink` do design (texto branco a 2,6:1 no rosa). */}
            <button type="button" aria-disabled={enviando || undefined} onClick={sair} className={`${BOTAO_DO_PAINEL} bg-urgent text-brand-white`}>
              Sair da ação
            </button>
          </div>
        </div>
      )}

      <div id={detalhesId}>
        {modelo.oppened && (
          <DetalhesDaAcaoEcoa
            data={data}
            getFormattedDate={modelo.getFormattedDate}
            responsavel={responsavel}
            // A linha do próprio usuário já tem "Sair da ação" no cartão; as demais seguem o `TaskItemEcoa` (gestor do integrante).
            acoesDoIntegrante={pessoa => pessoa._id !== chave && <ExclusaoDoIntegrante pessoa={pessoa} modelo={modelo} />}
          />
        )}
      </div>

      <button
        type="button"
        aria-expanded={modelo.oppened}
        aria-controls={detalhesId}
        onClick={() => modelo.setOppened(!modelo.oppened)}
        className={`mx-auto flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-[11px] font-bold uppercase tracking-[0.04em] text-content-soft ${FOCO_VISIVEL}`}
      >
        <span aria-hidden="true" className="text-[13px] leading-none">
          {modelo.oppened ? '−' : '+'}
        </span>
        {`Ver ${modelo.oppened ? 'menos' : 'mais'} informações`}
      </button>

      {/* Sempre montada: o leitor de tela anuncia a falha quando o texto entra (ECN-16, ECN-63). */}
      <p role="status" className="m-0 text-xs font-bold text-urgent">
        {falhou ? FALHA_NA_ACAO : ''}
      </p>

      <ModaisDaAcaoEcoa modelo={modelo} />
    </article>
  );
}
